import {
  DailyPoint,
  HybridMetrics,
  HybridNotPosted,
  HybridPlatform,
  HybridStructure,
  HybridTimeseries,
  ReportMetric,
  ReportPrefs,
  ReportRange,
  ReportRow,
  ReportSort,
  ReportTotals,
} from './hybrid-reporting.types';

const DAY_MS = 24 * 60 * 60 * 1000;

export const PLATFORM_LABEL: Record<HybridPlatform, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  youtube: 'YouTube',
  snapchat: 'Snapchat',
};

export const PLATFORM_LOGO: Record<HybridPlatform, string> = {
  tiktok: 'assets/social-icon/tik-tok-transparent.svg',
  instagram: 'assets/social-icon/instagram-logo.png',
  youtube: 'assets/social-icon/youtube_shorts.webp',
  snapchat: 'assets/social-icon/snapchat-logo.svg',
};

/** Social channel ids (SocialChannelEnum) → the placement's name. */
export const CHANNEL_LABEL: Record<number, string> = {
  0: 'TikTok',
  1: 'Instagram',
  2: 'Instagram Story',
  3: 'YouTube Shorts',
  4: 'Snapchat Spotlight',
  5: 'Snapchat Story',
};

export function channelLabel(channel: number): string {
  return CHANNEL_LABEL[Number(channel)] ?? 'Social';
}

/** Social channel ids (SocialChannelEnum) → platform. */
export function platformForChannel(channel: number): HybridPlatform | null {
  switch (Number(channel)) {
    case 0:
      return 'tiktok';
    case 1:
    case 2:
      return 'instagram';
    case 3:
      return 'youtube';
    case 4:
    case 5:
      return 'snapchat';
    default:
      return null;
  }
}

/** The platforms a brief asks for, in its channel order, without repeats. */
export function briefPlatforms(channels: readonly number[]): HybridPlatform[] {
  const out: HybridPlatform[] = [];
  for (const channel of channels) {
    const platform = platformForChannel(channel);
    if (platform && !out.includes(platform)) out.push(platform);
  }
  return out;
}

export type MetricDef = { key: ReportMetric; label: string; icon: string };
export const METRICS: readonly MetricDef[] = [
  { key: 'views', label: 'Views', icon: 'eye' },
  { key: 'engagements', label: 'Engagements', icon: 'zap' },
  { key: 'er', label: 'Engagement rate', icon: 'percent' },
  { key: 'likes', label: 'Likes', icon: 'heart' },
  { key: 'comments', label: 'Comments', icon: 'message-circle' },
  { key: 'shares', label: 'Shares', icon: 'share-2' },
  { key: 'saves', label: 'Saves', icon: 'bookmark' },
  { key: 'cpm', label: 'Cost per 1K views', icon: 'coins' },
];

export function metricLabel(metric: ReportMetric | 'posted'): string {
  if (metric === 'posted') return 'Posted';
  return METRICS.find((m) => m.key === metric)?.label ?? metric;
}

// ---------------------------------------------------------------------------
// Rows and totals
// ---------------------------------------------------------------------------

function sumKnown(...values: (number | null)[]): number | null {
  const known = values.filter((v): v is number => v !== null);
  return known.length ? known.reduce((a, b) => a + b, 0) : null;
}

/** Live posts joined with their creator and (when loaded) merged stats. */
export function buildRows(structure: HybridStructure, metrics: HybridMetrics | null): ReportRow[] {
  const creators = new Map(structure.creators.map((c) => [c.id, c]));
  return structure.posts.map((post) => {
    const stats = metrics?.posts[post.id] ?? null;
    const m = stats?.metrics;
    const views = m?.views ?? null;
    const likes = m?.likes ?? null;
    const comments = m?.comments ?? null;
    const shares = m?.shares ?? null;
    const saves = m?.saves ?? null;
    const engagements = sumKnown(likes, comments, shares, saves);
    return {
      post,
      creator: creators.get(post.creatorId) ?? null,
      stats,
      views,
      likes,
      comments,
      shares,
      saves,
      engagements,
      er: views && engagements !== null ? (engagements / views) * 100 : null,
      postedAt: stats?.postedAt ?? post.postedAt,
    };
  });
}

export function totals(rows: readonly ReportRow[]): ReportTotals {
  const t: ReportTotals = { n: rows.length, views: 0, likes: 0, comments: 0, shares: 0, saves: 0, engagements: 0, er: 0 };
  for (const r of rows) {
    t.views += r.views ?? 0;
    t.likes += r.likes ?? 0;
    t.comments += r.comments ?? 0;
    t.shares += r.shares ?? 0;
    t.saves += r.saves ?? 0;
    t.engagements += r.engagements ?? 0;
  }
  t.er = t.views ? (t.engagements / t.views) * 100 : 0;
  return t;
}

/** Brief-level cost per 1K views, in major units. Null when cost is hidden or there are no views. */
export function costPerThousand(paidMinor: number | null, views: number): number | null {
  if (paidMinor === null || !views) return null;
  return (paidMinor / 100 / views) * 1000;
}

export function filterRows(rows: readonly ReportRow[], platform: HybridPlatform | 'all', query: string): ReportRow[] {
  const q = query.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (platform === 'all' || r.post.platform === platform) &&
      (!q || (r.creator?.name ?? '').toLowerCase().includes(q)),
  );
}

/** Numbers high → low with unknowns last; Posted newest first. Ties keep creator name order. */
export function sortRows(rows: readonly ReportRow[], sort: ReportSort): ReportRow[] {
  const value = (r: ReportRow): number | null =>
    sort === 'posted' ? (r.postedAt ? new Date(r.postedAt).getTime() : null) : (r[sort] as number | null);
  return rows.slice().sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va === null && vb === null) return (a.creator?.name ?? '').localeCompare(b.creator?.name ?? '');
    if (va === null) return 1;
    if (vb === null) return -1;
    return vb - va || (a.creator?.name ?? '').localeCompare(b.creator?.name ?? '');
  });
}

/** Not-posted rows for the table: approved-and-waiting first, then by creator. */
export function notPostedRows(
  rows: readonly HybridNotPosted[],
  platform: HybridPlatform | 'all',
  query: string,
  creatorName: (id: string) => string,
): HybridNotPosted[] {
  const q = query.trim().toLowerCase();
  return rows
    .filter((r) => (platform === 'all' || r.platform === platform) && (!q || creatorName(r.creatorId).toLowerCase().includes(q)))
    .sort(
      (a, b) =>
        Number(b.videoApproved) - Number(a.videoApproved) || creatorName(a.creatorId).localeCompare(creatorName(b.creatorId)),
    );
}

// ---------------------------------------------------------------------------
// Chart
// ---------------------------------------------------------------------------

export type ChartPost = { creator: string; label: string; platform: HybridPlatform | null; postId: string };
export type ChartPoint = { date: string; value: number; posted: ChartPost[] };

export function utcDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(day: string, n: number): string {
  return utcDay(new Date(new Date(`${day}T00:00:00.000Z`).getTime() + n * DAY_MS));
}

type DayTotals = { views: number; likes: number; comments: number; shares: number; saves: number };

/**
 * A post's cumulative stats on `day`. With daily history: the last point on or before that day.
 * Without (history still loading, or neither source has it): 0 before it went live, today's
 * numbers from then on, so the running total still ends on the real figure.
 */
function postOnDay(row: ReportRow, series: DailyPoint[] | undefined, day: string): DayTotals {
  const zero = { views: 0, likes: 0, comments: 0, shares: 0, saves: 0 };
  const posted = row.postedAt ? row.postedAt.slice(0, 10) : null;
  if (series?.length && series.some((p) => p.views !== null || p.likes !== null)) {
    let point: DailyPoint | null = null;
    for (const p of series) {
      if (p.date <= day) point = p;
      else break;
    }
    if (!point) return zero;
    return {
      views: point.views ?? 0,
      likes: point.likes ?? 0,
      comments: point.comments ?? 0,
      shares: point.shares ?? 0,
      saves: point.saves ?? 0,
    };
  }
  if (!posted || posted > day) return zero;
  return { views: row.views ?? 0, likes: row.likes ?? 0, comments: row.comments ?? 0, shares: row.shares ?? 0, saves: row.saves ?? 0 };
}

function metricOf(t: DayTotals, metric: ReportMetric, paidMinor: number | null): number {
  const eng = t.likes + t.comments + t.shares + t.saves;
  switch (metric) {
    case 'engagements':
      return eng;
    case 'er':
      return t.views ? (eng / t.views) * 100 : 0;
    case 'cpm':
      return costPerThousand(paidMinor, t.views) ?? 0;
    default:
      return t[metric];
  }
}

/** Brief totals for `metric` on `day` across `rows`. */
export function totalOnDay(
  rows: readonly ReportRow[],
  timeseries: HybridTimeseries | null,
  metric: ReportMetric,
  day: string,
  paidMinor: number | null,
): number {
  const t: DayTotals = { views: 0, likes: 0, comments: 0, shares: 0, saves: 0 };
  for (const row of rows) {
    const d = postOnDay(row, timeseries?.posts[row.post.id], day);
    t.views += d.views;
    t.likes += d.likes;
    t.comments += d.comments;
    t.shares += d.shares;
    t.saves += d.saves;
  }
  return metricOf(t, metric, paidMinor);
}

/**
 * The running total of `metric` for each day from the first post (or the last 7 days) to today,
 * with the posts that went live each day.
 */
export function chartSeries(
  rows: readonly ReportRow[],
  timeseries: HybridTimeseries | null,
  metric: ReportMetric,
  range: ReportRange,
  today: string,
  paidMinor: number | null,
): ChartPoint[] {
  const postedDays = rows.map((r) => r.postedAt?.slice(0, 10)).filter((d): d is string => !!d).sort();
  if (!postedDays.length) return [];
  let start = range === '7' ? addDays(today, -6) : postedDays[0];
  if (start > today) start = today;
  // Always draw at least two days so a brief that started today still has a line.
  if (start === today) start = addDays(today, -1);

  const byDay = new Map<string, ChartPost[]>();
  for (const r of rows) {
    const day = r.postedAt?.slice(0, 10);
    if (!day) continue;
    const list = byDay.get(day) ?? [];
    list.push({ creator: r.creator?.name ?? 'Creator', label: r.post.deliverableLabel, platform: r.post.platform, postId: r.post.id });
    byDay.set(day, list);
  }

  const points: ChartPoint[] = [];
  for (let day = start; day <= today; day = addDays(day, 1)) {
    points.push({ date: day, value: totalOnDay(rows, timeseries, metric, day, paidMinor), posted: byDay.get(day) ?? [] });
  }
  return points;
}

/** "+n in the last 7 days" = total now − total 7 days ago. */
export function lastSevenDays(
  rows: readonly ReportRow[],
  timeseries: HybridTimeseries | null,
  metric: ReportMetric,
  today: string,
): number {
  return Math.max(0, totalOnDay(rows, timeseries, metric, today, null) - totalOnDay(rows, timeseries, metric, addDays(today, -7), null));
}

/** A rounded axis maximum: 1, 2, 2.5, 5 or 10 × a power of ten. */
export function niceMax(value: number): number {
  if (value <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(value)));
  const f = value / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
}

/** Banner while the first post is under 7 days old. */
export function showEarlyNumbers(rows: readonly ReportRow[], now: Date): boolean {
  const first = rows
    .map((r) => (r.postedAt ? new Date(r.postedAt).getTime() : NaN))
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => a - b)[0];
  return first !== undefined && now.getTime() - first < 7 * DAY_MS;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** 6900 · 12.8K (one decimal under 10K) · 48K · 1.2M. Null → "–". */
export function compact(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '–';
  const abs = Math.abs(n);
  if (abs < 10_000) return Math.round(n).toLocaleString('en-GB');
  if (abs < 1_000_000) {
    const k = n / 1000;
    return `${abs < 100_000 && k % 1 ? k.toFixed(1).replace(/\.0$/, '') : Math.round(k)}K`;
  }
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
}

export function full(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '–';
  return Math.round(n).toLocaleString('en-GB');
}

export function percent(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '–';
  return `${n.toFixed(1)}%`;
}

export function currencySymbol(code: string): string {
  switch ((code || '').toLowerCase()) {
    case 'usd':
      return '$';
    case 'eur':
      return '€';
    default:
      return '£';
  }
}

export function money(major: number | null | undefined, code: string): string {
  if (major === null || major === undefined || !Number.isFinite(major)) return '–';
  return `${currencySymbol(code)}${major.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatMetric(metric: ReportMetric, value: number | null, currencyCode: string): string {
  if (metric === 'er') return percent(value);
  if (metric === 'cpm') return money(value, currencyCode);
  return compact(value);
}

const WEEKDAY = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const SHORT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const SHORT_YEAR = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'UTC' });

/** "Today", "Yesterday", "Wed 29 Oct". */
export function relativeDay(iso: string | null | undefined, now: Date): string {
  if (!iso) return '–';
  const day = iso.slice(0, 10);
  const today = utcDay(now);
  if (day === today) return 'Today';
  if (day === addDays(today, -1)) return 'Yesterday';
  const label = WEEKDAY.format(new Date(`${day}T00:00:00.000Z`)).replace(',', '');
  return day.slice(0, 4) === today.slice(0, 4) ? label : `${label} ${day.slice(0, 4)}`;
}

/** "28 Oct", or "28 Oct 25" with `withYear`. */
export function shortDay(day: string, withYear = false): string {
  return (withYear ? SHORT_YEAR : SHORT).format(new Date(`${day.slice(0, 10)}T00:00:00.000Z`));
}

/** "just now", "1 hour ago", "{n} hours ago", "{n} days ago". */
export function ago(iso: string | null | undefined, now: Date): string {
  if (!iso) return 'not yet';
  const ms = now.getTime() - new Date(iso).getTime();
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours < 1) return 'just now';
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

// ---------------------------------------------------------------------------
// CSV and prefs
// ---------------------------------------------------------------------------

function csvCell(value: string | number | null): string {
  const s = value === null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows: readonly ReportRow[]): string {
  const header = ['Creator', 'Video', 'Platform', 'Posted', 'Views', 'Engagements', 'Likes', 'Comments', 'Shares', 'Saves', 'Engagement rate %', 'Ad code', 'Source', 'URL'];
  const lines = rows.map((r) =>
    [
      r.creator?.name ?? '',
      r.post.deliverableLabel,
      channelLabel(r.post.socialChannel),
      r.postedAt ? r.postedAt.slice(0, 10) : '',
      r.views,
      r.engagements,
      r.likes,
      r.comments,
      r.shares,
      r.saves,
      r.er === null ? null : Number(r.er.toFixed(2)),
      r.post.adCode,
      r.stats?.source === 'ultra' ? 'Ultra Social' : r.stats?.source === 'super' ? 'Super Social' : '',
      r.post.url,
    ]
      .map(csvCell)
      .join(','),
  );
  return [header.join(','), ...lines].join('\n');
}

export const DEFAULT_PREFS: ReportPrefs = {
  metric: 'views',
  sort: 'views',
  range: 'all',
  platform: 'all',
  showNotPosted: false,
  collapsed: false,
};

const PREFS_KEY = 'mbt-hybrid-reporting-v1';

export function readPrefs(userId: string | null): ReportPrefs {
  try {
    const raw = localStorage.getItem(`${PREFS_KEY}:${userId ?? 'anon'}`);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Partial<ReportPrefs>;
    return {
      metric: METRICS.some((m) => m.key === parsed.metric) ? parsed.metric! : DEFAULT_PREFS.metric,
      sort: parsed.sort ?? DEFAULT_PREFS.sort,
      range: parsed.range === '7' ? '7' : 'all',
      platform: parsed.platform ?? 'all',
      showNotPosted: !!parsed.showNotPosted,
      collapsed: !!parsed.collapsed,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function writePrefs(userId: string | null, prefs: ReportPrefs): void {
  try {
    localStorage.setItem(`${PREFS_KEY}:${userId ?? 'anon'}`, JSON.stringify(prefs));
  } catch {
    // Private mode or blocked storage: prefs just don't persist.
  }
}
